import { Controller } from "@hotwired/stimulus"

// Generic Grid Filter Controller
// Filters Grid rows based on search input and dropdown filters
// Works with any Grid component - fully reusable
export default class extends Controller {
  static targets = ["searchInput", "filterSelect", "clearButton"]
  static values = {
    gridSelector: String,  // CSS selector for the Grid to filter
    searchableFields: Array  // Array of data attributes to search in (e.g., ["name", "email"])
  }

  connect() {
    this.debounceTimer = null
    this.activeFilters = {
      search: "",
      dropdowns: {}
    }
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  // Search with debounce (300ms)
  search(event) {
    const searchTerm = event.target.value.toLowerCase().trim()

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Debounce search
    this.debounceTimer = setTimeout(() => {
      this.activeFilters.search = searchTerm
      this.applyFilters()
      this.updateUI()
    }, 300)
  }

  // Filter by dropdown (supports both single and multi-select)
  filter(event) {
    const select = event.target
    const filterName = select.dataset.filterName

    // Handle multi-select (Tom Select returns array of selected values)
    if (select.multiple) {
      const selectedOptions = Array.from(select.selectedOptions).map(opt => opt.value)

      if (selectedOptions.length === 0) {
        delete this.activeFilters.dropdowns[filterName]
      } else {
        this.activeFilters.dropdowns[filterName] = selectedOptions
      }
    } else {
      // Single select
      const filterValue = select.value

      if (filterValue === "all") {
        delete this.activeFilters.dropdowns[filterName]
      } else {
        this.activeFilters.dropdowns[filterName] = filterValue
      }
    }

    this.applyFilters()
    this.updateUI()
  }

  // Clear all filters
  clearFilters() {
    // Reset search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
    }

    // Reset all dropdowns (both regular and Tom Select)
    this.filterSelectTargets.forEach(select => {
      if (select.multiple) {
        // Tom Select multi-select: clear all selected options
        const tomSelectInstance = select.tomselect
        if (tomSelectInstance) {
          tomSelectInstance.clear()
        } else {
          // Fallback if Tom Select not initialized yet
          Array.from(select.options).forEach(opt => opt.selected = false)
        }
      } else {
        // Regular single select
        select.value = "all"
      }
    })

    // Clear active filters
    this.activeFilters = {
      search: "",
      dropdowns: {}
    }

    this.applyFilters()
    this.updateUI()
  }

  // Apply filters to Grid rows
  applyFilters() {
    const grid = this.getGridElement()
    if (!grid) return

    const rows = this.getGridRows(grid)

    rows.forEach(row => {
      if (this.rowMatchesFilters(row)) {
        this.showRow(row)
      } else {
        this.hideRow(row)
      }
    })

    // Dispatch custom event for other controllers (e.g., map-overlay)
    this.element.dispatchEvent(new CustomEvent("grid:filtered", {
      bubbles: true,
      detail: { filters: this.getActiveFilters() }
    }))
  }

  // Check if row matches all active filters
  rowMatchesFilters(row) {
    // Search filter
    if (this.activeFilters.search) {
      const searchMatch = this.rowMatchesSearch(row, this.activeFilters.search)
      if (!searchMatch) return false
    }

    // Dropdown filters
    for (const [filterName, filterValue] of Object.entries(this.activeFilters.dropdowns)) {
      const dropdownMatch = this.rowMatchesDropdown(row, filterName, filterValue)
      if (!dropdownMatch) return false
    }

    return true
  }

  // Check if row matches search term
  rowMatchesSearch(row, searchTerm) {
    // If searchableFields not configured, search in all data attributes
    const fieldsToSearch = this.hasSearchableFieldsValue ?
      this.searchableFieldsValue :
      this.getAllDataAttributes(row)

    return fieldsToSearch.some(field => {
      const value = row.dataset[field] || ""
      return value.toLowerCase().includes(searchTerm)
    })
  }

  // Check if row matches dropdown filter (supports array of values for multi-select)
  rowMatchesDropdown(row, filterName, filterValue) {
    const rowValue = row.dataset[filterName] || ""

    // If filterValue is an array (multi-select), check if rowValue matches ANY of the selected values
    if (Array.isArray(filterValue)) {
      return filterValue.some(val => this.valueContains(rowValue, val))
    }

    // Single value match - use contains for comma-separated values (like genres)
    return this.valueContains(rowValue, filterValue)
  }

  // Check if a row value contains the filter value
  // Handles both exact matches and comma-separated lists (e.g., "Death Metal, Black Metal")
  valueContains(rowValue, filterValue) {
    // Exact match
    if (rowValue === filterValue) return true

    // Check if filterValue is contained in comma-separated list
    // Split by comma, trim whitespace, and check for match
    const values = rowValue.split(',').map(v => v.trim())
    return values.includes(filterValue)
  }

  // Get all data attributes from a row
  getAllDataAttributes(row) {
    return Object.keys(row.dataset)
  }

  // Show row (works for both table rows and cards)
  showRow(row) {
    row.classList.remove("hidden")
    row.style.display = ""
  }

  // Hide row
  hideRow(row) {
    row.classList.add("hidden")
    row.style.display = "none"
  }

  // Get Grid element
  getGridElement() {
    if (this.hasGridSelectorValue) {
      return document.querySelector(this.gridSelectorValue)
    }

    // Default: find closest .grid-layout or table
    return this.element.closest("section")?.querySelector(".grid-layout, table") ||
           document.querySelector(".grid-layout, table")
  }

  // Get all filterable rows from Grid
  getGridRows(grid) {
    // Try table rows first
    const tableRows = Array.from(grid.querySelectorAll("tbody tr[data-venue-id], tbody tr[id]"))
    if (tableRows.length > 0) return tableRows

    // Try card layout
    const cards = Array.from(grid.querySelectorAll(".card[data-venue-id], .card[id]"))
    if (cards.length > 0) return cards

    // Fallback: any element with data attributes
    return Array.from(grid.querySelectorAll("[data-venue-id], [data-festival-id], [data-booking-id]"))
  }

  // Update UI (clear button)
  updateUI() {
    this.updateClearButton()
  }

  // Show/hide clear button
  updateClearButton() {
    if (!this.hasClearButtonTarget) return

    const hasActiveFilters = this.activeFilters.search ||
                            Object.keys(this.activeFilters.dropdowns).length > 0

    if (hasActiveFilters) {
      this.clearButtonTarget.classList.remove("hidden")
    } else {
      this.clearButtonTarget.classList.add("hidden")
    }
  }

  // Public API: Get active filters (for other controllers like map-overlay)
  getActiveFilters() {
    return {
      search: this.activeFilters.search,
      filters: this.activeFilters.dropdowns
    }
  }

  // Public API: Set filters (from external source like map)
  setFilters(filters) {
    // Set search
    if (filters.search && this.hasSearchInputTarget) {
      this.searchInputTarget.value = filters.search
      this.activeFilters.search = filters.search
    }

    // Set dropdowns
    if (filters.filters) {
      this.activeFilters.dropdowns = { ...filters.filters }

      this.filterSelectTargets.forEach(select => {
        const filterName = select.dataset.filterName
        if (filters.filters[filterName]) {
          select.value = filters.filters[filterName]
        }
      })
    }

    this.applyFilters()
    this.updateUI()
  }
}
